<script>
import { defineComponent } from "vue";

import { CLASS_NAME_COMPONENT, createBlockClasses } from "./common";
import { color, presentational, size, state, variant } from "./props";

export default defineComponent({
  name: "TgIconButton",
  props: { color, presentational, size, state, variant },
  computed: {
    variantClass() {
      return `${CLASS_NAME_COMPONENT}-${this.variant}`;
    },

    componentClasses() {
      return [
        createBlockClasses(CLASS_NAME_COMPONENT, { size: this.size }),
        createBlockClasses(this.variantClass, {
          color: this.color,
          size: this.size,
          state: this.state
        })
      ];
    },

    presentationalAttrs() {
      if (!this.presentational) {
        return {};
      }

      return {
        tabindex: "-1",
        role: "presentation",
        ariaHidden: true
      };
    }
  }
});
</script>

<template>
  <button :class="componentClasses" v-bind="presentationalAttrs">
    <slot />
  </button>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@disabled-color: rgb(@theme-colors[@grey][50]);
@disabled-bg-color: rgb(@theme-colors[@grey][25]);

.tg-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 100ms ease-in-out;
  border-radius: 50%;
  vertical-align: middle;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &--sm {
    width: 1.25rem;
    min-width: 1.25rem;
    height: 1.25rem;
    min-height: 1.25rem;
  }

  &--md {
    width: 1.75rem;
    min-width: 1.75rem;
    height: 1.75rem;
    min-height: 1.75rem;
  }

  &--lg,
  &--xl,
  &--xxl {
    width: 2.75rem;
    min-width: 2.75rem;
    height: 2.75rem;
    min-height: 2.75rem;
  }
}

.tg-icon-button-positive {
  border: none;

  &:disabled {
    background-color: transparent;
    color: @disabled-color;

    &:hover {
      background-color: @disabled-bg-color;
      color: @disabled-color;
    }
  }

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      @color: rgb(@value[base]);
      @bg-color: rgb(@value[10]);

      border: 1px solid transparent;
      background-color: transparent;
      color: @color;

      &:hover:not(:disabled):not(:focus),
      &-hovered {
        border-color: transparent;
        background-color: @bg-color;
        color: @color;
      }

      &:focus,
      &-focused {
        border-color: @color;
        background-color: @bg-color;
        color: @color;
      }
    }
  });
}

.tg-icon-button-negative {
  @color: @colors[@white];
  @bg-color: rgba(@colors[@black], 0.2);

  border: 1px solid transparent;
  background-color: transparent;
  color: @color;

  // Negative only uses b/w colors, but needs the correct class names to set the styles correctly
  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      &:hover:not(:disabled):not(:focus),
      &-hovered {
        border-color: transparent;
        background-color: @bg-color;
        color: @color;
      }

      &:focus,
      &-focused {
        border-color: @color;
        background-color: @bg-color;
        color: @color;
      }

      &:disabled {
        background-color: transparent;
        color: rgba(@color, 0.5);

        &:hover {
          background-color: @bg-color;
          color: rgba(@color, 0.5);
        }
      }
    }
  });
}

.tg-icon-button-solid {
  .outline(@shadow-color, @shadow-color-dark, @dark-blur-radius) {
    box-shadow:
      5px 5px 10px 0 @shadow-color,
      0 0 0 @dark-blur-radius @shadow-color-dark;
  }

  // Ensures correct sizing
  box-sizing: content-box;

  &:disabled {
    @disabled-bg-color: rgb(@theme-colors[@grey][50]);
    border: 2px solid @disabled-bg-color;

    background-color: @disabled-bg-color;
    box-shadow: none;
    color: @colors[@white];
  }

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      @bg-color: rgb(@value[base]);
      @bg-dark-color: rgb(@value[110]);
      @shadow-color: rgba(@value[base], 0.2);
      @shadow-color-dark: rgba(@value[base], 0.3);

      background-color: @bg-color;
      border: 2px solid @bg-color;
      box-shadow: 5px 5px 10px 0 @shadow-color;
      color: @colors[ @white];

      &:hover:not(:disabled):not(:focus),
      &-hovered {
        background-color: @bg-dark-color;
        border-color: @bg-dark-color;
        .outline(@shadow-color, @bg-dark-color, 1px);
      }

      &-sm:focus,
      &-sm-focused  {
        .outline(@shadow-color, @shadow-color-dark, 2px);
      }

      &-md:focus,
      &-md-focused {
        .outline(@shadow-color, @shadow-color-dark, 3px);
      }

      &-lg:focus,
      &-lg-focused {
        .outline(@shadow-color, @shadow-color-dark, 4px);
      }
    }
  });
}
</style>
